import React from "react";
import {
  setPaymentMethod,
  inputValidation,
  clearValidation,
  setLoaderCheckout,
  setLoader,
  saveGiftCardOrder,
  clearGiftCardDetails,
  setEmptyCart,
  // New Amplify actions
  createPaymentIntent,
  createOrderAmplify,
  getOrCreateStripeCustomer,
  fetchSavedCardsAmplify,
} from "../actions";
import { connect } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";
import history from "../history";
import { getReCaptchaToken } from "../helper/recaptcha";

class CheckoutCards extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showAddNewCard: false,
      stripeCustomerId: null,
      saveCardForFuture: true,
    };
  }

  componentDidMount = async () => {
    // Get or create Stripe customer when component mounts
    if (this.props.auth.is_signed_in) {
      const mobile = localStorage.mobile || this.props.auth.mobile || '';
      const email = localStorage.email || this.props.auth.email || '';
      const name = `${this.props.auth.first_name || ''} ${this.props.auth.last_name || ''}`.trim();

      this.props.getOrCreateStripeCustomer(mobile, email, name, (result) => {
        if (result?.success && result?.stripeCustomerId) {
          this.setState({ stripeCustomerId: result.stripeCustomerId });

          // Fetch saved cards using the Stripe customer ID
          this.props.fetchSavedCardsAmplify(result.stripeCustomerId, () => {
            this.props.setLoaderCheckout("saved_cards");
          });
        } else {
          this.props.setLoaderCheckout("saved_cards");
        }
      });
    } else {
      this.props.setLoaderCheckout("saved_cards");
    }
  };

  // Calculate cart totals from localStorage
  calculateTotals = () => {
    const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const shippingAmount = this.props.shipping_method?.amount || 0;
    const taxAmount = 0; // GST included in prices
    const grandTotal = subtotal + shippingAmount;

    return { subtotal, shippingAmount, taxAmount, grandTotal };
  };

  handleSubmit = async (event) => {
    let input_validation = {};
    const { stripe, elements } = this.props;

    // Get shipping address from props or localStorage
    let shippingAddress = this.props.selected_shipping_address;
    if (!shippingAddress) {
      const storedAddress = localStorage.getItem("selectedShippingAddress");
      if (storedAddress) {
        shippingAddress = JSON.parse(storedAddress);
      }
    }

    // Validate shipping address
    if (!shippingAddress) {
      input_validation.address = ["Please complete your shipping address"];
    }

    let pm = this.props.payment_card.pm;

    // For new card, create payment method first
    if (pm === "new_card") {
      if (!stripe || !elements) {
        input_validation.card = ["Payment system not ready. Please refresh and try again."];
        this.props.inputValidation(input_validation);
        return;
      }

      this.props.setLoader(true, "Validating Card...");
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: {
          email: localStorage.email || this.props.auth.email || '',
          name: `${shippingAddress?.firstname || ''} ${shippingAddress?.lastname || ''}`.trim(),
        },
      });

      this.props.setLoader(false, null);

      if (error) {
        input_validation.card = [error.message];
        this.props.inputValidation(input_validation);
        return;
      }

      pm = paymentMethod.id;
    }

    if (!pm) {
      input_validation.card = ["Please select or enter a card for payment"];
    }

    if (Object.keys(input_validation).length > 0) {
      this.props.inputValidation(input_validation);
      return;
    }

    this.props.clearValidation();
    this.props.handleCancelTimer();

    // Check for gift cards in cart
    const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    const has_gift_card = cartItems.some((p) => {
      if (p.options) {
        return p.options.some((o) => o.attribute_code === "gift_card_price");
      }
      return p.is_gift_card || p.is_giftcard;
    });

    // Calculate totals
    const { subtotal, shippingAmount, taxAmount, grandTotal } = this.calculateTotals();
    const sellerId = this.props.seller?.seller_id;
    const customerEmail = localStorage.email || this.props.auth.email || '';

    if (!sellerId) {
      this.props.inputValidation({ card: ["Unable to process payment. Seller information missing."] });
      return;
    }

    this.props.setLoader(true, "Processing Payment...");

    // Determine if this is a saved card or new card
    const savedCards = this.props.saved_cards || [];
    const isSavedCard = pm !== "new_card" && savedCards.some(c => c.id === pm);

    // Create PaymentIntent
    this.props.createPaymentIntent(
      sellerId,
      grandTotal,
      customerEmail,
      this.state.stripeCustomerId,
      isSavedCard ? pm : null, // Only pass paymentMethodId for saved cards
      this.state.saveCardForFuture && !isSavedCard, // Save new cards if checkbox is checked
      async (result) => {
        if (!result?.success) {
          this.props.setLoader(false, null);
          return;
        }

        let paymentSucceeded = false;
        let paymentIntentId = result.paymentIntentId;

        // If saved card was used, payment is already confirmed server-side
        if (isSavedCard) {
          if (result.status === 'succeeded' || result.status === 'requires_capture') {
            paymentSucceeded = true;
          } else if (result.status === 'requires_action') {
            // 3D Secure required even for saved card
            this.props.setLoader(true, "Confirming Payment...");
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
              result.clientSecret
            );
            if (confirmError) {
              this.props.setLoader(false, null);
              this.props.inputValidation({ card: [confirmError.message] });
              return;
            }
            paymentSucceeded = paymentIntent.status === 'succeeded';
          } else {
            this.props.setLoader(false, null);
            this.props.inputValidation({ card: ["Payment failed. Please try again."] });
            return;
          }
        } else {
          // For new card, confirm payment client-side
          this.props.setLoader(true, "Confirming Payment...");

          const confirmOptions = {
            payment_method: pm,
          };

          // Save card for future if checkbox is checked and we have a customer
          if (this.state.saveCardForFuture && this.state.stripeCustomerId) {
            confirmOptions.setup_future_usage = 'off_session';
          }

          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            result.clientSecret,
            confirmOptions
          );

          if (confirmError) {
            this.props.setLoader(false, null);
            this.props.inputValidation({ card: [confirmError.message] });
            return;
          }

          paymentSucceeded = paymentIntent.status === 'succeeded';
          paymentIntentId = paymentIntent.id;
        }

        if (!paymentSucceeded) {
          this.props.setLoader(false, null);
          this.props.inputValidation({ card: ["Payment was not completed. Please try again."] });
          return;
        }

        // Payment succeeded - create order in DynamoDB
        this.props.setLoader(true, "Creating Order...");

        // Get billing address from props or localStorage (defaults to shipping address)
        let billingAddress = this.props.billing_address;
        if (!billingAddress) {
          const storedBilling = localStorage.getItem("billingAddress");
          if (storedBilling) {
            billingAddress = JSON.parse(storedBilling);
          } else {
            billingAddress = shippingAddress; // Use shipping as billing
          }
        }

        const orderData = {
          sellerId,
          customer: {
            mobile: localStorage.mobile || this.props.auth.mobile || '',
            email: customerEmail,
            first_name: billingAddress?.firstname || shippingAddress?.firstname || localStorage.first_name || '',
            last_name: billingAddress?.lastname || shippingAddress?.lastname || localStorage.last_name || '',
          },
          shippingAddress: shippingAddress ? {
            firstname: shippingAddress.firstname,
            lastname: shippingAddress.lastname,
            street: shippingAddress.street,
            city: shippingAddress.city,
            region: shippingAddress.region?.region || shippingAddress.region || '',
            region_code: shippingAddress.region?.region_code || shippingAddress.region_code || shippingAddress.regionCode || '',
            postcode: shippingAddress.postcode,
            country_id: shippingAddress.country_id || shippingAddress.countryId || 'AU',
            telephone: shippingAddress.telephone,
          } : null,
          billingAddress: billingAddress ? {
            firstname: billingAddress.firstname,
            lastname: billingAddress.lastname,
            street: billingAddress.street,
            city: billingAddress.city,
            region: billingAddress.region?.region || billingAddress.region || '',
            region_code: billingAddress.region?.region_code || billingAddress.region_code || billingAddress.regionCode || '',
            postcode: billingAddress.postcode,
            country_id: billingAddress.country_id || billingAddress.countryId || 'AU',
            telephone: billingAddress.telephone,
          } : null,
          items: cartItems.map(item => ({
            product_id: item.id || item.product_id,
            sku: item.sku,
            name: item.name || item.product_name,
            qty: item.qty,
            price: item.price,
            product_type: item.product_type || 'simple',
            image_url: item.image || item.image_url,
          })),
          subtotal,
          shippingAmount,
          taxAmount,
          grandTotal,
          shippingMethod: this.props.shipping_method?.method_code || 'standard',
          stripePaymentIntentId: paymentIntentId,
          stripeCustomerId: this.state.stripeCustomerId,
          shopName: this.props.seller?.shop_title || this.props.seller?.name || '',
        };

        this.props.createOrderAmplify(orderData, (orderResult) => {
          if (!orderResult?.success) {
            this.props.setLoader(false, null);
            return;
          }

          // Clear cart
          localStorage.removeItem('cartItems');
          delete localStorage.campaign_id;

          // Handle gift cards if present
          if (has_gift_card && this.props.gift_card_details.length > 0) {
            const cart_id = this.props.gift_card_details[0].cart_id;
            getReCaptchaToken('checkout', (token) => {
              const giftCardData = {
                r_token: token,
                cart_id: cart_id,
                order_id: orderResult.incrementId || orderResult.orderId,
              };
              this.props.saveGiftCardOrder(giftCardData, () => {
                this.props.clearGiftCardDetails();
                this.props.setLoader(false, null);
              });
            });
          } else {
            this.props.setLoader(false, null);
          }

          // Redirect to success page
          history.push('/success');
        });
      }
    );
  };

  renderSavedCards = () => {
    let check_first = this.state.showAddNewCard ? false : true;
    const savedCards = this.props.saved_cards || [];
    const card_rows = savedCards.map((v, i, a) => {
      let month = v.exp_month < 10 ? "0" + v.exp_month : v.exp_month;
      let year = v.exp_year.toString().substring(2, 4);
      let expiry = month + "/" + year;

      let checked = false;
      if (check_first && i === 0) {
        checked = true;
      }

      return (
        <div key={v.id} className="checkout-card">
          <div className="card-radio-parent">
            <input
              type="radio"
              name="card"
              defaultChecked={checked}
              className="card-radio"
              checked={this.props.payment_card.pm === v.id}
              onClick={() => {
                this.props.setPaymentMethod(v.id);
              }}
            />
          </div>
          <div className="card-number-parent">
            <div className="card-number">
              {v.brand && <span className="card-brand">{v.brand}</span>} ****{v.last4}
            </div>
          </div>
          <div className="card-expiry-parent">
            <div className="card-expiry">exp {expiry}</div>
          </div>
        </div>
      );
    });

    if (this.state.showAddNewCard) {
      card_rows[card_rows.length] = (
        <div key="new_card" className="checkout-card">
          <div className="card-radio-parent">
            <input
              type="radio"
              name="card"
              defaultChecked={true}
              id="card-radio-new"
              onClick={() => {
                this.props.setPaymentMethod("new_card");
              }}
            />
          </div>
          <div id="card-new-card-heading">
            <div id="add-new-card-heading">Add New Card</div>
          </div>
        </div>
      );
    }

    return card_rows;
  };

  render() {
    let cardMessage = (this.props.input_validation?.card || []).map((message) => (
      <div key={message} className="input-validation">
        {message}
      </div>
    ));

    return (
      <React.Fragment>
        <div id="checkout-payment">
          <div id="checkout-payment-heading">Payment Information</div>
          <div id="checkout-cards">{this.renderSavedCards()}</div>
          {this.state.showAddNewCard && (
            <div id="card-new-card-inputs" className="form">
              <div className="input-box">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                      },
                    },
                  }}
                />
              </div>
              {this.state.stripeCustomerId && (
                <div className="save-card-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={this.state.saveCardForFuture}
                      onChange={(e) => this.setState({ saveCardForFuture: e.target.checked })}
                    />
                    Save card for future purchases
                  </label>
                </div>
              )}
            </div>
          )}
          {!this.state.showAddNewCard && (
            <div
              className="payment-add-update"
              onClick={() => {
                this.setState({ showAddNewCard: true });
                this.props.setPaymentMethod("new_card");
              }}
            >
              Add New
            </div>
          )}
          <div id="payment-failed-message">{cardMessage}</div>
          <div
            className="checkout-arrow"
            onClick={() => {
              if (!this.state.showAddNewCard) {
                this.props.setPaymentMethod("new_card");
              } else {
                if (this.props.saved_cards && this.props.saved_cards.length > 0 && this.props.saved_cards[0]) {
                  this.props.setPaymentMethod(this.props.saved_cards[0].id);
                } else {
                  this.props.setPaymentMethod("new_card");
                }
              }

              this.setState({ showAddNewCard: !this.state.showAddNewCard });
            }}
          ></div>
        </div>
        <div id="checkout-confirm-purchase">
          <button
            type="submit"
            disabled={this.props.disable_checkout_button}
            onClick={(e) => {
              this.handleSubmit(e);
            }}
          >
            Confirm Purchase
          </button>
          <img
            id="checkout-lock-logo"
            src="https://cdn-staging.securemobile.net/web/images/SecureMobile-lock-logo.png"
            alt="Secure checkout"
          />
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    saved_cards: state.saved_cards.saved_cards,
    input_validation: state.input_validation,
    billing_address: state.billing_address.billing_address,
    cart: state.cart.cart,
    payment_card: state.payment_card,
    shipping_addresses: state.shipping_addresses,
    shipping_method: state.shipping_method.shipping_method,
    selected_shipping_address:
      state.selected_shipping_address.selected_shipping_address,
    seller: state.seller.seller,
    auth: state.auth,
    disable_checkout_button: state.disable_checkout_button,
    affiliate_seller_id: state.affiliate_seller_id,
    cart_products: state.cart_products.cart_products,
    gift_card_details: state.gift_card_details.gift_card_details,
  };
};

const ReadyCheckoutCards = connect(mapStateToProps, {
  setPaymentMethod,
  inputValidation,
  clearValidation,
  setLoaderCheckout,
  setLoader,
  saveGiftCardOrder,
  clearGiftCardDetails,
  setEmptyCart,
  // New Amplify actions
  createPaymentIntent,
  createOrderAmplify,
  getOrCreateStripeCustomer,
  fetchSavedCardsAmplify,
})(CheckoutCards);

const InjectedCheckoutCards = ({ handleCancelTimer }) => (
  <ElementsConsumer>
    {({ stripe, elements }) => (
      <ReadyCheckoutCards
        stripe={stripe}
        elements={elements}
        handleCancelTimer={handleCancelTimer}
      />
    )}
  </ElementsConsumer>
);

// Use environment variable for Stripe publishable key
const getStripePublishableKey = () => {
  if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  }

  // No fallback - must be configured via environment variable
  console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY not set!');
  return '';
};

const stripePromise = loadStripe(getStripePublishableKey());

const ReadyRegistration = ({ handleCancelTimer }) => (
  <Elements stripe={stripePromise}>
    <InjectedCheckoutCards handleCancelTimer={handleCancelTimer} />
  </Elements>
);

export default ReadyRegistration;
